import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchShotRawData, fetchEquipments } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import './MachineDataView.css';

// 컬럼 정의: [key, i18n labelKey, 단위]
const COLUMNS = [
  ['_equipmentName', 'pages.monitoring.machineDataView.cols.equipmentName', ''],
  ['shotCount', 'pages.monitoring.machineDataView.cols.shotCount', ''],
  ['shotTime', 'pages.monitoring.machineDataView.cols.shotTime', ''],
  ['craftId', 'pages.monitoring.machineDataView.cols.craftId', ''],
  ['cycleTime', 'pages.monitoring.machineDataView.cols.cycleTime', 's'],
  ['injTime', 'pages.monitoring.machineDataView.cols.injTime', 's'],
  ['holdTime', 'pages.monitoring.machineDataView.cols.holdTime', 's'],
  ['chargeTime', 'pages.monitoring.machineDataView.cols.chargeTime', 's'],
  ['moldOpenTime', 'pages.monitoring.machineDataView.cols.moldOpenTime', 's'],
  ['moldCloseTime', 'pages.monitoring.machineDataView.cols.moldCloseTime', 's'],
  ['fetchTime', 'pages.monitoring.machineDataView.cols.fetchTime', 's'],
  ['ejectTime', 'pages.monitoring.machineDataView.cols.ejectTime', 's'],
  ['highPressureTime', 'pages.monitoring.machineDataView.cols.highPressureTime', 's'],
  ['moldProtectTime', 'pages.monitoring.machineDataView.cols.moldProtectTime', 's'],
  ['suckbackTime', 'pages.monitoring.machineDataView.cols.suckbackTime', 's'],
  ['injMaxPress', 'pages.monitoring.machineDataView.cols.injMaxPress', 'Bar'],
  ['injPress', 'pages.monitoring.machineDataView.cols.injPress', 'Bar'],
  ['injSpeed', 'pages.monitoring.machineDataView.cols.injSpeed', 'mm/s'],
  ['injStartPosi', 'pages.monitoring.machineDataView.cols.injStartPosi', 'mm'],
  ['injEndPosi', 'pages.monitoring.machineDataView.cols.injEndPosi', 'mm'],
  ['injPosi', 'pages.monitoring.machineDataView.cols.injPosi', 'mm'],
  ['turnPosi', 'pages.monitoring.machineDataView.cols.turnPosi', 'mm'],
  ['turnPress', 'pages.monitoring.machineDataView.cols.turnPress', 'Bar'],
  ['turnTime', 'pages.monitoring.machineDataView.cols.turnTime', 's'],
  ['holdPress', 'pages.monitoring.machineDataView.cols.holdPress', 'Bar'],
  ['holdSpeed', 'pages.monitoring.machineDataView.cols.holdSpeed', ''],
  ['holdPosi', 'pages.monitoring.machineDataView.cols.holdPosi', 'mm'],
  ['chargePress', 'pages.monitoring.machineDataView.cols.chargePress', 'Bar'],
  ['chargeSpeed', 'pages.monitoring.machineDataView.cols.chargeSpeed', ''],
  ['chargePosi', 'pages.monitoring.machineDataView.cols.chargePosi', 'mm'],
  ['backPress', 'pages.monitoring.machineDataView.cols.backPress', 'Bar'],
  ['temp1', 'pages.monitoring.machineDataView.cols.temp1', '℃'],
  ['temp2', 'pages.monitoring.machineDataView.cols.temp2', '℃'],
  ['temp3', 'pages.monitoring.machineDataView.cols.temp3', '℃'],
  ['temp4', 'pages.monitoring.machineDataView.cols.temp4', '℃'],
  ['temp5', 'pages.monitoring.machineDataView.cols.temp5', '℃'],
  ['oilTemp', 'pages.monitoring.machineDataView.cols.oilTemp', '℃'],
  ['qualityStatus', 'pages.monitoring.machineDataView.cols.qualityStatus', ''],
  ['anomalyScore', 'pages.monitoring.machineDataView.cols.anomalyScore', ''],
];

const PAGE_SIZES = [100, 200, 500, 1000];

function MachineDataView() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const equipmentId = searchParams.get('id');

  const [machines, setMachines] = useState([]);
  const [selectedId, setSelectedId] = useState(equipmentId ? Number(equipmentId) : null); // null = 전체
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(''); // 진행 상태 텍스트
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCraft, setSelectedCraft] = useState('');
  const [isCraftDropdownOpen, setIsCraftDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const craftDropdownRef = useRef(null);
  const tableWrapRef = useRef(null);

  // 장비 목록 조회
  useEffect(() => {
    fetchEquipments()
      .then(data => {
        if (Array.isArray(data)) {
          setMachines(data);
          // id 파라미터 없으면 전체 조회
          if (!equipmentId && data.length > 0) {
            setSelectedId(null);
          }
        }
      })
      .catch(err => console.error('장비 목록 조회 실패:', err));
  }, []);

  // 데이터 조회: 설비 선택 또는 전체
  useEffect(() => {
    if (machines.length === 0) return;
    setLoading(true);
    setCurrentPage(1);
    setSelectedCraft('');

    if (selectedId) {
      // 특정 설비 조회
      const machine = machines.find(m => m.id === selectedId);
      setLoadingProgress(t('pages.monitoring.machineDataView.loading'));
      fetchShotRawData(selectedId, 10000)
        .then(data => {
          if (data) {
            const rows = (data.rows || []).map(r => ({
              ...r,
              _equipmentName: machine?.equipmentName || `#${selectedId}`,
              _equipmentId: selectedId,
            }));
            setAllRows(rows);
          }
        })
        .catch(err => console.error('데이터 조회 실패:', err))
        .finally(() => { setLoading(false); setLoadingProgress(''); });
    } else {
      // 전체 설비 점진적 로딩: 응답 오는 대로 바로 표시
      setAllRows([]);
      let loaded = 0;
      const total = machines.length;
      setLoadingProgress(t('pages.monitoring.machineDataView.loadingProgressInit', { total }));

      machines.forEach(m => {
        fetchShotRawData(m.id, 2000)
          .then(data => {
            if (data) {
              const rows = (data.rows || []).map(r => ({
                ...r,
                _equipmentName: m.equipmentName,
                _equipmentId: m.id,
              }));
              setAllRows(prev => {
                const merged = [...prev, ...rows];
                merged.sort((a, b) => {
                  if (a.shotTime && b.shotTime) return b.shotTime.localeCompare(a.shotTime);
                  return 0;
                });
                return merged;
              });
            }
          })
          .catch(() => {})
          .finally(() => {
            loaded++;
            if (loaded < total) {
              setLoadingProgress(t('pages.monitoring.machineDataView.loadingProgress', { loaded, total }));
            } else {
              setLoading(false);
              setLoadingProgress('');
            }
          });
      });
    }
  }, [selectedId, machines, t]);

  // 드롭다운 외부 클릭
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isDropdownOpen]);

  // 금형 드롭다운 외부 클릭
  useEffect(() => {
    const handler = (e) => {
      if (craftDropdownRef.current && !craftDropdownRef.current.contains(e.target)) {
        setIsCraftDropdownOpen(false);
      }
    };
    if (isCraftDropdownOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isCraftDropdownOpen]);

  const selectedMachine = machines.find(m => m.id === selectedId);

  // 금형ID 목록 추출
  const craftIds = [...new Set(allRows.map(r => r.craftId).filter(Boolean))].sort();

  const handleSelect = (id) => {
    setSelectedId(id);
    if (id) {
      setSearchParams({ id: String(id) });
    } else {
      setSearchParams({});
    }
    setIsDropdownOpen(false);
  };

  const formatVal = (val) => {
    if (val == null) return '-';
    if (typeof val === 'number') return val.toLocaleString();
    return String(val);
  };

  // 금형 필터 적용
  const filteredRows = selectedCraft
    ? allRows.filter(r => r.craftId === selectedCraft)
    : allRows;

  // 페이징 계산
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const displayRows = filteredRows.slice(startIdx, startIdx + pageSize);

  const handleCraftSelect = (craft) => {
    setSelectedCraft(craft);
    setCurrentPage(1);
    setIsCraftDropdownOpen(false);
    if (tableWrapRef.current) tableWrapRef.current.scrollTop = 0;
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    if (tableWrapRef.current) tableWrapRef.current.scrollTop = 0;
  };

  const goPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    if (tableWrapRef.current) tableWrapRef.current.scrollTop = 0;
  };

  return (
    <div className="mdv-container">
      {/* 상단 바 */}
      <div className="mdv-header">
        <div className="mdv-header-left">
          <span className="material-symbols-outlined mdv-title-icon">database</span>
          <h1 className="mdv-title">{t('pages.monitoring.machineDataView.title')}</h1>
        </div>
        <div className="mdv-selector" ref={dropdownRef}>
          <button className="mdv-selector-btn" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <span className="mdv-selector-name">{selectedMachine?.equipmentName || t('pages.monitoring.machineDataView.allMachines')}</span>
            <span className="mdv-selector-sub">
              {selectedMachine ? `${selectedMachine.manufacturer} · ${selectedMachine.model}` : t('pages.monitoring.machineDataView.machineCount', { n: machines.length })}
            </span>
            <span className="material-symbols-outlined">
              {isDropdownOpen ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {isDropdownOpen && (
            <div className="mdv-dropdown">
              <div
                className={`mdv-dropdown-item ${!selectedId ? 'active' : ''}`}
                onClick={() => handleSelect(null)}
              >
                <span className="mdv-dropdown-name">{t('pages.monitoring.machineDataView.allMachines')}</span>
                <span className="mdv-dropdown-model">{t('pages.monitoring.machineDataView.machineCount', { n: machines.length })}</span>
              </div>
              {machines.map(m => (
                <div
                  key={m.id}
                  className={`mdv-dropdown-item ${selectedId === m.id ? 'active' : ''}`}
                  onClick={() => handleSelect(m.id)}
                >
                  <span className="mdv-dropdown-name">{m.equipmentName}</span>
                  <span className="mdv-dropdown-model">{m.manufacturer} · {m.model}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* 금형 선택 */}
        {craftIds.length > 0 && (
          <div className="mdv-selector mdv-craft-selector" ref={craftDropdownRef}>
            <button className="mdv-selector-btn" onClick={() => setIsCraftDropdownOpen(!isCraftDropdownOpen)}>
              <span className="mdv-selector-name">{selectedCraft || t('pages.monitoring.machineDataView.allMolds')}</span>
              <span className="material-symbols-outlined">
                {isCraftDropdownOpen ? 'expand_less' : 'expand_more'}
              </span>
            </button>
            {isCraftDropdownOpen && (
              <div className="mdv-dropdown">
                <div
                  className={`mdv-dropdown-item ${!selectedCraft ? 'active' : ''}`}
                  onClick={() => handleCraftSelect('')}
                >
                  <span className="mdv-dropdown-name">{t('pages.monitoring.machineDataView.allMolds')}</span>
                  <span className="mdv-dropdown-model">{t('pages.monitoring.machineDataView.countSuffix', { n: allRows.length.toLocaleString() })}</span>
                </div>
                {craftIds.map(craft => (
                  <div
                    key={craft}
                    className={`mdv-dropdown-item ${selectedCraft === craft ? 'active' : ''}`}
                    onClick={() => handleCraftSelect(craft)}
                  >
                    <span className="mdv-dropdown-name">{craft}</span>
                    <span className="mdv-dropdown-model">
                      {t('pages.monitoring.machineDataView.countSuffix', { n: allRows.filter(r => r.craftId === craft).length.toLocaleString() })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <span className="mdv-count">
          {selectedCraft
            ? t('pages.monitoring.machineDataView.filteredTotal', { filtered: filteredRows.length.toLocaleString(), total: allRows.length.toLocaleString() })
            : t('pages.monitoring.machineDataView.total', { n: allRows.length.toLocaleString() })}
        </span>

        {/* 페이지 사이즈 선택 */}
        <div className="mdv-page-sizes">
          {PAGE_SIZES.map(size => (
            <button
              key={size}
              className={`mdv-page-size-btn ${pageSize === size ? 'active' : ''}`}
              onClick={() => handlePageSizeChange(size)}
            >
              {size}
            </button>
          ))}
        </div>

        {loading && <span className="mdv-loading">{loadingProgress || t('pages.monitoring.machineDataView.loading')}</span>}
      </div>

      {/* 데이터 테이블 */}
      <div className="mdv-body">
        {loading && allRows.length > 0 && (
          <div className="mdv-loading-overlay">
            <span className="material-symbols-outlined mdv-spin">progress_activity</span>
            <span>{loadingProgress || t('pages.monitoring.machineDataView.loading')}</span>
          </div>
        )}
        {loading && allRows.length === 0 ? (
          <div className="mdv-empty">
            <span className="material-symbols-outlined mdv-spin" style={{ fontSize: 28 }}>progress_activity</span>
            <div>{loadingProgress || t('pages.monitoring.machineDataView.loadingDataInit')}</div>
          </div>
        ) : !loading && allRows.length === 0 ? (
          <div className="mdv-empty">{t('pages.monitoring.machineDataView.empty')}</div>
        ) : (
          <div className="mdv-table-wrap" ref={tableWrapRef}>
            <table className="mdv-table">
              <thead>
                <tr>
                  <th className="mdv-th mdv-th-row">#</th>
                  {COLUMNS.map(([key, labelKey, unit]) => (
                    <th key={key} className="mdv-th">
                      <div className="mdv-th-content">
                        <span>{t(labelKey)}</span>
                        {unit && <span className="mdv-th-unit">{unit}</span>}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, idx) => (
                  <tr key={`${row._equipmentId}-${row.shotCount}-${idx}`}>
                    <td className="mdv-td mdv-td-row">{startIdx + idx + 1}</td>
                    {COLUMNS.map(([key]) => (
                      <td
                        key={key}
                        className={`mdv-td ${key === 'qualityStatus' && (row[key] === 'BAD' || row[key] === 'DEFECT') ? 'mdv-td-bad' : ''} ${key === '_equipmentName' ? 'mdv-td-equip' : ''}`}
                      >
                        {formatVal(row[key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 페이징 바 */}
      {filteredRows.length > 0 && (
        <div className="mdv-paging">
          <button className="mdv-page-btn" onClick={() => goPage(1)} disabled={currentPage === 1}>
            <span className="material-symbols-outlined">first_page</span>
          </button>
          <button className="mdv-page-btn" onClick={() => goPage(currentPage - 1)} disabled={currentPage === 1}>
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
          <span className="mdv-page-info">
            {t('pages.monitoring.machineDataView.pageInfo', {
              start: startIdx + 1,
              end: Math.min(startIdx + pageSize, filteredRows.length),
              total: filteredRows.length.toLocaleString(),
            })}
          </span>
          <button className="mdv-page-btn" onClick={() => goPage(currentPage + 1)} disabled={currentPage === totalPages}>
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
          <button className="mdv-page-btn" onClick={() => goPage(totalPages)} disabled={currentPage === totalPages}>
            <span className="material-symbols-outlined">last_page</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default MachineDataView;
